import React, { useState } from "react";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input
} from "@heroui/react";
import { Partner, staffApi } from "../../../../api/staff";
import { useTranslations } from "../../../../contexts/translation.context";
import { showPartnerSuccess, showError } from "../../../../utils/toast";
import { ConfirmModal } from "../../../../components";

interface PartnerActionsCellProps {
  partner: Partner;
  onRefresh?: () => void;
}

const PartnerActionsCell: React.FC<PartnerActionsCellProps> = ({ partner, onRefresh }) => {
  const { t } = useTranslations();
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    action: 'approve' | 'reject' | 'deactivate' | null;
  }>({
    isOpen: false,
    action: null,
  });
  const [balanceModal, setBalanceModal] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState("");

  const handleAction = async (key: React.Key) => {
    const action = key as string;

    if (action === 'topUpBalance') {
      setBalanceAmount("");
      setBalanceModal(true);
      return;
    }

    if (action === 'deactivate') {
      setConfirmModal({ isOpen: true, action });
    } else {
      await performAction(action as 'approve' | 'reject' | 'deactivate');
    }
  };

  const handleTopUpBalance = async () => {
    const amount = parseFloat(balanceAmount);
    if (isNaN(amount) || amount === 0) {
      showError(t('admin.partners.actions.topUpBalance.invalidAmount'));
      return;
    }

    setIsProcessing(true);
    try {
      await staffApi.changePartnerBalance(partner.id, amount);
      showPartnerSuccess(t('admin.partners.actions.topUpBalance.success'));
      setBalanceModal(false);
      setBalanceAmount("");
      onRefresh?.();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.message
        || error?.message
        || t('admin.partners.actions.error');
      showError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const performAction = async (action: 'approve' | 'reject' | 'deactivate') => {
    console.log('Performing action:', action, 'for partner:', partner);
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      switch (action) {
        case 'approve':
          console.log('Approving partner, setting status to: active');
          await staffApi.changePartnerStatus(partner.id, 'active');
          // Если деактивированный партнер активируется
          if (partner.status === 'deactivated') {
            showPartnerSuccess(t('admin.partners.actions.activateSuccess'));
          } else {
            showPartnerSuccess(t('admin.partners.actions.approveSuccess'));
          }
          break;
        case 'reject':
          console.log('Rejecting partner, setting status to: wait_moderation');
          await staffApi.changePartnerStatus(partner.id, 'wait_moderation');
          showPartnerSuccess(t('admin.partners.actions.rejectSuccess'));
          break;
        case 'deactivate':
          console.log('Deactivating partner, setting status to: deactivated');
          await staffApi.changePartnerStatus(partner.id, 'deactivated');
          showPartnerSuccess(t('admin.partners.actions.deactivateSuccess'));
          break;
      }
      onRefresh?.();
    } catch (error: any) {
      console.error('Action failed:', action);
      console.error('Error details:', {
        message: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
        detail: error?.response?.data?.detail
      });

      // Извлекаем детальное сообщение об ошибке из ответа API
      const errorMessage = error?.response?.data?.detail
        || error?.response?.data?.detail?.[0]?.msg
        || error?.response?.data?.message
        || error?.message
        || t('admin.partners.actions.error');
      showError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirm = () => {
    if (confirmModal.action) {
      performAction(confirmModal.action);
    }
  };

  // Determine which actions to show based on status
  const isWaitModeration = partner.status === 'wait_moderation';
  const isDeactivated = partner.status === 'deactivated';

  return (
    <>
      <div className="flex justify-end">
        <Dropdown>
          <DropdownTrigger>
            <Button
              size="sm"
              variant="light"
              aria-label="More options"
              className="min-w-[32px] w-[32px] h-[32px] p-0"
              isDisabled={isProcessing}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 5.83333C10.4603 5.83333 10.8333 5.46033 10.8333 5C10.8333 4.53967 10.4603 4.16667 10 4.16667C9.53968 4.16667 9.16668 4.53967 9.16668 5C9.16668 5.46033 9.53968 5.83333 10 5.83333Z" fill="#71717A"/>
                <path d="M10 10.8333C10.4603 10.8333 10.8333 10.4603 10.8333 10C10.8333 9.53967 10.4603 9.16667 10 9.16667C9.53968 9.16667 9.16668 9.53967 9.16668 10C9.16668 10.4603 9.53968 10.8333 10 10.8333Z" fill="#71717A"/>
                <path d="M10 15.8333C10.4603 15.8333 10.8333 15.4603 10.8333 15C10.8333 14.5397 10.4603 14.1667 10 14.1667C9.53968 14.1667 9.16668 14.5397 9.16668 15C9.16668 15.4603 9.53968 15.8333 10 15.8333Z" fill="#71717A"/>
              </svg>
            </Button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label="Partner actions"
            onAction={handleAction}
          >
            <DropdownItem key="topUpBalance">
              {t('admin.partners.actions.topUpBalance.title')}
            </DropdownItem>
            {isWaitModeration && (
              <>
                <DropdownItem key="approve">
                  {t('admin.partners.actions.approve')}
                </DropdownItem>
                <DropdownItem key="reject">
                  {t('admin.partners.actions.reject')}
                </DropdownItem>
              </>
            )}
            {isDeactivated ? (
              <DropdownItem key="approve" className="text-success" color="success">
                {t('admin.partners.actions.activate')}
              </DropdownItem>
            ) : (
              !isWaitModeration && (
                <DropdownItem key="deactivate" className="text-danger" color="danger">
                  {t('admin.partners.actions.deactivate')}
                </DropdownItem>
              )
            )}
          </DropdownMenu>
        </Dropdown>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null })}
        onConfirm={handleConfirm}
        title={t('admin.partners.actions.deactivate')}
        message={`${t('admin.partners.actions.deactivateConfirm')} "${partner.name}"?`}
        confirmText={t('admin.partners.actions.deactivate')}
        cancelText={t('global.cancel')}
        confirmColor="danger"
        isLoading={isProcessing}
      />

      <Modal isOpen={balanceModal} onClose={() => setBalanceModal(false)} size="md">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {t('admin.partners.actions.topUpBalance.title')}
          </ModalHeader>
          <ModalBody>
            <p className="text-[14px] text-[#71717A] mb-4">
              {t('admin.partners.actions.topUpBalance.description')} <strong>{partner.name}</strong>
            </p>
            <Input
              type="number"
              label={t('admin.partners.actions.topUpBalance.amountLabel')}
              placeholder={t('admin.partners.actions.topUpBalance.amountPlaceholder')}
              value={balanceAmount}
              onValueChange={setBalanceAmount}
              startContent={
                <span className="text-[#71717A] text-[14px]">$</span>
              }
              classNames={{
                input: "text-[14px]",
                label: "text-[14px] font-medium"
              }}
            />
            <p className="text-[12px] text-[#71717A] mt-2">
              {t('admin.partners.actions.topUpBalance.hint')}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="light"
              onPress={() => setBalanceModal(false)}
              isDisabled={isProcessing}
            >
              {t('global.cancel')}
            </Button>
            <Button
              color="primary"
              onPress={handleTopUpBalance}
              isLoading={isProcessing}
            >
              {t('admin.partners.actions.topUpBalance.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PartnerActionsCell;
